import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Upload, RefreshCw, X, FileImage, Trash2 } from "lucide-react";
import { api, setToken as setApiToken, inr, split4060 } from "@/api";
import { BlogManager } from "@/components/BlogManager";
import { ReviewsManager } from "@/components/ReviewsManager";

const inputCls =
    "w-full px-3 py-2.5 rounded-lg bg-white border border-[#E6DBCF] text-sm text-[#241D19] focus:outline-none focus:ring-2 focus:ring-[#C85A17]/40";
const STATUSES = ["Pending", "Advance Paid", "Confirmed", "Completed", "Cancelled"];
const STAY_STATUSES = ["New", "Contacted", "Booked", "Closed"];

export default function Admin() {
    const [token, setTokenState] = useState(localStorage.getItem("ds_admin_token") || "");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [tab, setTab] = useState("bookings");
    const [bookings, setBookings] = useState([]);
    const [stays, setStays] = useState([]);
    const [packages, setPackages] = useState([]);
    const [settings, setSettings] = useState(null);
    const [edits, setEdits] = useState({});
    const [proofView, setProofView] = useState(null);

    const logout = () => {
        localStorage.removeItem("ds_admin_token");
        setApiToken(null);
        setTokenState("");
    };

    const loadAll = async () => {
        try {
            const [b, p, s, st] = await Promise.all([
                api.get("/admin/bookings"),
                api.get("/packages"),
                api.get("/settings"),
                api.get("/admin/stay-requests"),
            ]);
            setBookings(b.data);
            setPackages(p.data);
            setSettings(s.data);
            setStays(st.data);
        } catch (e) {
            if ([401, 403].includes(e.response?.status)) logout();
        }
    };

    useEffect(() => {
        if (token) {
            setApiToken(token);
            localStorage.setItem("ds_admin_token", token);
            loadAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setTokenState(data.token);
            toast.success("Welcome back, Admin");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login failed");
        }
    };

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/bookings/${id}`, { status });
            setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
            toast.success("Status updated");
        } catch {
            toast.error("Could not update status");
        }
    };

    const setStayStatus = async (id, status) => {
        try {
            await api.patch(`/admin/stay-requests/${id}`, { status });
            setStays((ss) => ss.map((s) => (s.id === id ? { ...s, status } : s)));
            toast.success("Status updated");
        } catch {
            toast.error("Could not update status");
        }
    };

    const deleteBooking = async (id) => {
        if (!window.confirm("Delete this booking permanently?")) return;
        try {
            await api.delete(`/admin/bookings/${id}`);
            setBookings((bs) => bs.filter((b) => b.id !== id));
            toast.success("Booking deleted");
        } catch {
            toast.error("Could not delete booking");
        }
    };

    const deleteStay = async (id) => {
        if (!window.confirm("Delete this stay request permanently?")) return;
        try {
            await api.delete(`/admin/stay-requests/${id}`);
            setStays((ss) => ss.filter((s) => s.id !== id));
            toast.success("Stay request deleted");
        } catch {
            toast.error("Could not delete stay request");
        }
    };

    const viewProof = async (id) => {
        try {
            const { data } = await api.get(`/admin/bookings/${id}/proof`);
            setProofView({ id, data: data.proof_data });
        } catch {
            toast.error("Could not load payment proof");
        }
    };

    const edit = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [k]: v } }));

    const savePackage = async (pkg) => {
        const e = edits[pkg.id] || {};
        const payload = {
            name: e.name ?? pkg.name,
            duration: e.duration ?? pkg.duration,
            tag: e.tag ?? pkg.tag,
            description: e.description ?? pkg.description,
            price: e.price !== undefined && e.price !== "" ? Number(e.price) : pkg.price,
        };
        try {
            const { data } = await api.put(`/admin/packages/${pkg.id}`, payload);
            setPackages((ps) => ps.map((p) => (p.id === pkg.id ? data : p)));
            toast.success("Package saved");
        } catch {
            toast.error("Could not save package");
        }
    };

    const uploadImage = async (pkgId, file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/admin/packages/${pkgId}/image`, fd);
            toast.success("Image uploaded");
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        }
    };

    const uploadQr = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post("/admin/settings/qr", fd);
            toast.success("QR updated");
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post("/admin/settings/logo", fd);
            toast.success("Logo updated");
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        }
    };

    const saveSettings = async () => {
        try {
            await api.put("/admin/settings", {
                whatsapp: settings.whatsapp,
                bank_name: settings.bank_name,
                account_name: settings.account_name,
                account_number: settings.account_number,
                ifsc: settings.ifsc,
            });
            toast.success("Settings saved");
        } catch {
            toast.error("Could not save settings");
        }
    };

    if (!token) {
        return (
            <div data-testid="admin-login-page" className="min-h-screen bg-[#FAF6F0] flex items-center justify-center px-6">
                <form onSubmit={login} className="w-full max-w-sm bg-white rounded-2xl border border-[#E6DBCF] p-8 shadow-lg">
                    <img src="/logo.jpg" alt="Dharm Sarovar" className="h-14 w-14 rounded-xl object-cover border border-[#E5C158] mb-4" />
                    <p className="font-display font-semibold text-lg text-[#241D19]">DHARM SAROVAR</p>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#C85A17] mt-1 mb-6">Admin Login</p>
                    <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">Email</label>
                    <input data-testid="admin-email-input" className={inputCls} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                    <label className="text-xs font-medium text-[#6E6259] mt-4 mb-1.5 block">Password</label>
                    <input data-testid="admin-password-input" className={inputCls} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                    <button data-testid="admin-login-button" className="mt-6 w-full py-3 rounded-full bg-[#C85A17] text-white font-semibold hover:bg-[#9C370B] transition-colors">
                        Login
                    </button>
                    <a href="/" className="mt-4 block text-center text-xs text-[#6E6259] hover:text-[#C85A17]">← Back to website</a>
                </form>
            </div>
        );
    }

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF6F0]">
            <header className="bg-white border-b border-[#E6DBCF] px-6 md:px-12 h-16 flex items-center justify-between sticky top-0 z-40">
                <p className="font-display font-semibold text-[#241D19]">DHARM SAROVAR <span className="text-xs text-[#C85A17] tracking-[0.2em] ml-2">ADMIN</span></p>
                <div className="flex items-center gap-3">
                    <button onClick={loadAll} data-testid="admin-refresh-button" className="p-2 rounded-full hover:bg-[#F4EDE2] text-[#6E6259]" aria-label="Refresh">
                        <RefreshCw size={17} />
                    </button>
                    <a href="/" className="text-xs text-[#6E6259] hover:text-[#C85A17]">View site</a>
                    <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-xs text-[#6E6259] hover:text-[#C85A17]">
                        <LogOut size={15} /> Logout
                    </button>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
                <div className="flex flex-wrap gap-2 mb-8">
                    {["bookings", "stays", "blog", "reviews", "packages", "settings"].map((t) => (
                        <button
                            key={t}
                            data-testid={`admin-tab-${t}`}
                            onClick={() => setTab(t)}
                            className={`px-5 py-2.5 rounded-full text-sm font-medium capitalize transition-colors ${
                                tab === t ? "bg-[#241D19] text-white" : "bg-white border border-[#E6DBCF] text-[#6E6259] hover:text-[#241D19]"
                            }`}
                        >
                            {t === "stays" ? "Stay Requests" : t}
                            {t === "bookings" && bookings.length > 0 ? ` (${bookings.length})` : ""}
                            {t === "stays" && stays.length > 0 ? ` (${stays.length})` : ""}
                        </button>
                    ))}
                </div>

                <div data-testid="admin-overview" className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[
                        { label: "Total Bookings", value: bookings.length, testid: "admin-stat-bookings" },
                        { label: "Pending Bookings", value: bookings.filter((b) => b.status === "Pending").length, testid: "admin-stat-pending" },
                        { label: "Stay Requests", value: stays.length, testid: "admin-stat-stays" },
                        { label: "Ritual Value Booked", value: inr(bookings.reduce((a, b) => a + (b.total || 0), 0)), testid: "admin-stat-value" },
                    ].map((c) => (
                        <div key={c.label} className="bg-white border border-[#E6DBCF] rounded-2xl p-5">
                            <p className="font-display text-2xl font-semibold text-[#9C370B]" data-testid={c.testid}>{c.value}</p>
                            <p className="text-xs text-[#6E6259] mt-1 uppercase tracking-wider">{c.label}</p>
                        </div>
                    ))}
                </div>

                {tab === "stays" && (
                    <div data-testid="admin-stays-list" className="space-y-4">
                        {stays.length === 0 && (
                            <p className="text-sm text-[#6E6259] bg-white border border-[#E6DBCF] rounded-2xl p-8 text-center">
                                No stay requests yet. Accommodation assistance requests will appear here.
                            </p>
                        )}
                        {stays.map((s) => (
                            <div key={s.id} data-testid={`admin-stay-${s.id}`} className="bg-white border border-[#E6DBCF] rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4">
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-[#241D19]">{s.name}</span>
                                        <span className="text-xs text-[#6E6259]">#{s.id}</span>
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFF8E7] border border-[#E5C158] text-[#9C370B]">{s.stay_type}</span>
                                    </div>
                                    <p className="text-sm text-[#6E6259] mt-1">
                                        {s.checkin || "—"} → {s.checkout || "—"} · {s.phone}{s.members ? ` · ${s.members} guests` : ""}
                                    </p>
                                    {s.notes && <p className="text-xs text-[#6E6259] mt-1 italic">"{s.notes}"</p>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <select
                                        data-testid={`admin-stay-status-${s.id}`}
                                        value={s.status}
                                        onChange={(e) => setStayStatus(s.id, e.target.value)}
                                        className={`${inputCls} md:w-40`}
                                    >
                                        {STAY_STATUSES.map((st) => <option key={st}>{st}</option>)}
                                    </select>
                                    <button
                                        data-testid={`admin-stay-delete-${s.id}`}
                                        onClick={() => deleteStay(s.id)}
                                        className="p-2.5 rounded-lg border border-[#E6DBCF] text-[#9C370B] hover:bg-red-50 transition-colors"
                                        aria-label="Delete stay request"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "bookings" && (
                    <div data-testid="admin-bookings-list" className="space-y-4">
                        {bookings.length === 0 && (
                            <p className="text-sm text-[#6E6259] bg-white border border-[#E6DBCF] rounded-2xl p-8 text-center">
                                No bookings yet. New booking requests will appear here.
                            </p>
                        )}
                        {bookings.map((b) => (
                            <div key={b.id} data-testid={`admin-booking-${b.id}`} className="bg-white border border-[#E6DBCF] rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4">
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-[#241D19]">{b.name}</span>
                                        <span className="text-xs text-[#6E6259]">#{b.id}</span>
                                        {b.accommodation && (
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFF8E7] border border-[#E5C158] text-[#9C370B]">Stay &amp; Food</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-[#6E6259] mt-1">
                                        {b.package_name} · {b.date} · {b.phone}{b.members ? ` · ${b.members} members` : ""}{b.stay_type && b.stay_type !== "no" ? ` · Stay: ${b.stay_type === "ac" ? "AC ₹5,000/day" : "Non-AC ₹3,000/day"}` : ""}
                                    </p>
                                    <p className="text-xs text-[#6E6259] mt-1">
                                        Total {inr(b.total)} · Advance {inr(b.advance)} · Balance {inr(b.balance)}
                                        {b.payment_id ? ` · Paid: ${b.payment_id}` : ""}{b.payment_option ? ` · ${b.payment_option}` : ""}{b.utr ? ` · UTR: ${b.utr}` : ""}
                                    </p>
                                    <p className="text-[10px] text-[#B0A494] mt-1">
                                        Received: {new Date(b.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </p>
                                    {b.notes && <p className="text-xs text-[#6E6259] mt-1 italic">"{b.notes}"</p>}
                                    {b.has_proof && (
                                        <button
                                            onClick={() => viewProof(b.id)}
                                            data-testid={`admin-booking-proof-${b.id}`}
                                            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#C85A17] hover:text-[#9C370B]"
                                        >
                                            <FileImage size={14} /> View Payment Proof
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <select
                                        data-testid={`admin-booking-status-${b.id}`}
                                        value={b.status}
                                        onChange={(e) => setStatus(b.id, e.target.value)}
                                        className={`${inputCls} md:w-44`}
                                    >
                                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                    <button
                                        data-testid={`admin-booking-delete-${b.id}`}
                                        onClick={() => deleteBooking(b.id)}
                                        className="p-2.5 rounded-lg border border-[#E6DBCF] text-[#9C370B] hover:bg-red-50 transition-colors"
                                        aria-label="Delete booking"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "blog" && <BlogManager />}

                {tab === "reviews" && <ReviewsManager />}

                {tab === "packages" && (
                    <div data-testid="admin-packages-list" className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {packages.map((pkg) => {
                            const e = edits[pkg.id] || {};
                            return (
                                <div key={pkg.id} data-testid={`admin-package-${pkg.id}`} className="bg-white border border-[#E6DBCF] rounded-2xl p-5">
                                    <div className="flex gap-4">
                                        <img src={pkg.image} alt="" className="w-20 h-20 rounded-xl object-cover border border-[#E6DBCF] shrink-0" />
                                        <div className="flex-1 space-y-2">
                                            <input className={inputCls} value={e.name ?? pkg.name} onChange={(ev) => edit(pkg.id, "name", ev.target.value)} data-testid={`admin-package-name-${pkg.id}`} />
                                            <div className="grid grid-cols-3 gap-2">
                                                <input className={inputCls} value={e.duration ?? pkg.duration} onChange={(ev) => edit(pkg.id, "duration", ev.target.value)} placeholder="Duration" />
                                                <input className={inputCls} type="number" value={e.price ?? pkg.price} onChange={(ev) => edit(pkg.id, "price", ev.target.value)} placeholder="Price ₹" data-testid={`admin-package-price-${pkg.id}`} />
                                                <input className={inputCls} value={e.tag ?? pkg.tag} onChange={(ev) => edit(pkg.id, "tag", ev.target.value)} placeholder="Tag" />
                                            </div>
                                        </div>
                                    </div>
                                    <textarea className={`${inputCls} mt-3 min-h-[64px]`} value={e.description ?? pkg.description} onChange={(ev) => edit(pkg.id, "description", ev.target.value)} />
                                    <p className="text-xs text-[#6E6259] mt-2">
                                        40% advance {inr(split4060(Number(e.price ?? pkg.price)).advance)} · 60% balance {inr(split4060(Number(e.price ?? pkg.price)).balance)}
                                    </p>
                                    <div className="mt-3 flex items-center gap-3">
                                        <label className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E6DBCF] text-xs font-medium text-[#241D19] cursor-pointer hover:bg-[#F4EDE2]">
                                            <Upload size={14} /> Upload Image
                                            <input type="file" accept="image/*" className="hidden" data-testid={`admin-package-image-${pkg.id}`} onChange={(ev) => uploadImage(pkg.id, ev.target.files[0])} />
                                        </label>
                                        <button onClick={() => savePackage(pkg)} data-testid={`admin-package-save-${pkg.id}`} className="px-5 py-2.5 rounded-full bg-[#C85A17] text-white text-xs font-semibold hover:bg-[#9C370B] transition-colors">
                                            Save
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {tab === "settings" && settings && (
                    <div data-testid="admin-settings-panel" className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <div className="bg-white border border-[#E6DBCF] rounded-2xl p-6 space-y-4">
                            <h3 className="font-serif-alt font-semibold text-xl text-[#241D19]">Contact &amp; Bank</h3>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1 block">WhatsApp Number (with country code)</label>
                                <input className={inputCls} value={settings.whatsapp} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} data-testid="admin-settings-whatsapp" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1 block">Account Name</label>
                                <input className={inputCls} value={settings.account_name} onChange={(e) => setSettings({ ...settings, account_name: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#6E6259] mb-1 block">Bank Name</label>
                                <input className={inputCls} value={settings.bank_name} onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-[#6E6259] mb-1 block">Account Number</label>
                                    <input className={inputCls} value={settings.account_number} onChange={(e) => setSettings({ ...settings, account_number: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[#6E6259] mb-1 block">IFSC</label>
                                    <input className={inputCls} value={settings.ifsc} onChange={(e) => setSettings({ ...settings, ifsc: e.target.value })} />
                                </div>
                            </div>
                            <button onClick={saveSettings} data-testid="admin-settings-save-button" className="px-6 py-3 rounded-full bg-[#C85A17] text-white text-sm font-semibold hover:bg-[#9C370B] transition-colors">
                                Save Settings
                            </button>
                        </div>
                        <div className="bg-white border border-[#E6DBCF] rounded-2xl p-6">
                            <h3 className="font-serif-alt font-semibold text-xl text-[#241D19]">UPI QR Code</h3>
                            <p className="text-xs text-[#6E6259] mt-1 mb-4">Shown in the Payment section. Upload your official business QR image.</p>
                            <img src={settings.qr_data || "/upi-qr.jpg"} alt="UPI QR" className="w-48 rounded-xl border border-[#E6DBCF]" data-testid="admin-qr-preview" />
                            <label className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E6DBCF] text-xs font-medium text-[#241D19] cursor-pointer hover:bg-[#F4EDE2]">
                                <Upload size={14} /> Replace QR Image
                                <input type="file" accept="image/*" className="hidden" data-testid="admin-qr-upload-input" onChange={(e) => uploadQr(e.target.files[0])} />
                            </label>
                        </div>
                        <div className="bg-white border border-[#E6DBCF] rounded-2xl p-6">
                            <h3 className="font-serif-alt font-semibold text-xl text-[#241D19]">Company Logo</h3>
                            <p className="text-xs text-[#6E6259] mt-1 mb-4">Shown in the navbar and footer of the website.</p>
                            <img src={settings.logo_data || "/logo.jpg"} alt="Logo" className="w-28 h-28 rounded-xl object-cover border border-[#E6DBCF]" data-testid="admin-logo-preview" />
                            <label className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E6DBCF] text-xs font-medium text-[#241D19] cursor-pointer hover:bg-[#F4EDE2]">
                                <Upload size={14} /> Replace Logo
                                <input type="file" accept="image/*" className="hidden" data-testid="admin-logo-upload-input" onChange={(e) => uploadLogo(e.target.files[0])} />
                            </label>
                        </div>
                    </div>
                )}
            </div>

            {proofView && (
                <div
                    data-testid="admin-proof-modal"
                    className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm"
                    onClick={() => setProofView(null)}
                >
                    <div className="relative bg-white rounded-2xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setProofView(null)}
                            data-testid="admin-proof-close-button"
                            className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-[#241D19] text-white flex items-center justify-center"
                            aria-label="Close"
                        >
                            <X size={16} />
                        </button>
                        <p className="text-xs text-[#6E6259] mb-3">Payment proof — Booking #{proofView.id}</p>
                        <img src={proofView.data} alt="Payment proof" className="w-full rounded-xl border border-[#E6DBCF]" />
                    </div>
                </div>
            )}
        </div>
    );
}
